// Since types is the only package shared between server-core, api, and app, we implement text normalization logic here.

/**
 * Common abbreviations used for text normalization in field comparison.
 * Covers street types, directionals, and geographic features.
 *
 * Based on USPS Postal Addressing Standards Publication 28, Appendix C
 * https://pe.usps.com/text/pub28/28apc_002.htm
 *
 * Used by normalizeText() in this file for aggressive text normalization in field comparison.
 */
const commonAbbreviations: Record<string, string> = {
  // Common street types
  avenue: 'ave',
  street: 'st',
  road: 'rd',
  boulevard: 'blvd',
  drive: 'dr',
  lane: 'ln',
  loop: 'lp',
  court: 'ct',
  circle: 'cir',
  place: 'pl',
  terrace: 'ter',
  parkway: 'pkwy',
  highway: 'hwy',
  square: 'sq',
  trail: 'trl',
  way: 'way',
  view: 'vw',
  alley: 'aly',
  expressway: 'expy',
  freeway: 'fwy',
  junction: 'jct',
  turnpike: 'tpke',

  // Directionals
  north: 'n',
  south: 's',
  east: 'e',
  west: 'w',
  northeast: 'ne',
  northwest: 'nw',
  southeast: 'se',
  southwest: 'sw',

  // Geographic features
  mount: 'mt',
  fort: 'ft',
  heights: 'hts',
  creek: 'crk',
  crossing: 'xing',
  plaza: 'plz',
  gardens: 'gdns',
  grove: 'grv',
  harbor: 'hbr',
  hills: 'hls',
  island: 'is',
  meadows: 'mdws',
  ridge: 'rdg',
  valley: 'vly',
  village: 'vlg',
  walk: 'walk',
  woods: 'wds',
};

export const normalizeText = (text: string): string => {
  // Lowercase first for consistent matching
  let normalized = text.toLowerCase();

  // Remove periods first so that abbreviations with periods (e.g., "ave.") match correctly
  normalized = normalized.replace(/\./g, '');

  // Replace abbreviations with their full forms and vice versa, using word boundaries
  Object.entries(commonAbbreviations).forEach(([full, abbr]) => {
    // Use word boundaries to avoid partial matches inside other words
    const fullPattern = new RegExp(`\\b${full}\\b`, 'g');
    const abbrPattern = new RegExp(`\\b${abbr}\\b`, 'g');
    // Create a standardized form by replacing both with the abbreviation
    normalized = normalized.replace(fullPattern, abbr).replace(abbrPattern, abbr);
  });

  // Remove remaining whitespace, dashes, commas
  normalized = normalized.replace(/[,\-\s]/g, '');

  return normalized;
};

export const compareNormalizedValues = (actual: any, expected: any, ignoreNullExpected = false): boolean => {
  if (!expected) {
    // If ignoreNullExpected is true, pass the comparison when expected is null/undefined/empty
    return ignoreNullExpected;
  }
  // Handle pipe-delimited alternatives in expected value (e.g. "Barnegat|barnegat Township")
  if (typeof expected === 'string' && expected.includes('|')) {
    return expected.split('|').some((alt) => compareNormalizedValues(actual, alt.trim(), ignoreNullExpected));
  }
  const actualNormalized = normalizeText(String(actual || ''));
  const expectedNormalized = normalizeText(String(expected || ''));
  return actualNormalized === expectedNormalized;
};
